/**
 * OverflowIndicator — "+N more" button with a Radix Popover that lists
 * all cards for a given date. The popover floats over the calendar grid,
 * showing full card titles that are clickable to open the card detail.
 *
 * Validates: Requirements 7.1, 7.3, 7.5, 10.8
 */

import React, { useMemo } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { CardChip } from './CardChip';
import { sortCardsForDay, formatDateForAria } from '../../utils/date-utils';
import type { CardSummary, ListSummary } from '../../state/active-board-context';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OverflowIndicatorProps {
  readonly hiddenCount: number;
  readonly allCards: readonly CardSummary[];
  readonly lists: readonly ListSummary[];
  readonly date: string;
  readonly onCardClick: (cardId: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OverflowIndicator({
  hiddenCount,
  allCards,
  lists,
  date,
  onCardClick,
}: OverflowIndicatorProps): React.ReactElement {
  const sortedCards = useMemo(() => sortCardsForDay(allCards, lists), [allCards, lists]);

  const listColorMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const list of lists) {
      map.set(list.listId, list.color);
    }
    return map;
  }, [lists]);

  const dateLabel = formatDateForAria(date);

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="text-xs text-primary font-medium w-full text-left
            cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring
            rounded px-1 py-0.5 hover:bg-accent transition-colors"
          aria-label={`Show ${hiddenCount} more cards for ${dateLabel}`}
        >
          +{hiddenCount} more
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          aria-label={`All cards for ${dateLabel}`}
          className="z-[100] w-80 max-h-96 overflow-y-auto rounded-lg border
            border-border bg-popover text-popover-foreground p-4 shadow-xl
            focus:outline-none animate-in fade-in-0 zoom-in-95 slide-in-from-top-2"
          sideOffset={-20}
          align="start"
          collisionPadding={16}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground">{dateLabel}</h3>
            <span className="text-xs text-muted-foreground">{sortedCards.length} cards</span>
          </div>

          {/* Card list */}
          <div className="flex flex-col gap-1.5">
            {sortedCards.map((card) => (
              <CardChip
                key={card.cardId}
                card={card}
                listColor={listColorMap.get(card.listId) ?? '#6b7280'}
                onClick={() => onCardClick(card.cardId)}
              />
            ))}
          </div>

          {/* Arrow pointing to trigger */}
          <Popover.Arrow className="fill-popover" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
